import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useHeroVideo } from '../hooks/useHeroVideo';
import { Link } from 'react-router-dom';
import {
    ChevronRight, ChevronDown, Calendar, BookOpen, Play, HardDrive,
    Server, LayoutDashboard, UserCheck, Trophy, Puzzle, Send,
    BarChart2, Bomb, Target, Shield, Crosshair, Map, Database,
    Eye, Zap, CheckCircle2, Image as ImageIcon, Swords, Search, Monitor, PlusCircle, LogIn, Wrench, Globe
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import './LoginPage.css';
import { LEGAL_CONTENT, markdownToHtml } from './LegalPage';
import { PREMIUM_TEAM_SCOUT_MAX_DEMOS_PER_ANALYSIS } from '../config/scoutCapacity';

const API = import.meta.env.VITE_API_URL || 'http://localhost:3001';

// ─── Animated Counter Component ───────────────────────────────
function AnimatedCounter({ target, suffix = '', duration = 2000 }: { target: number; suffix?: string; duration?: number }) {
    const [count, setCount] = useState(0);
    const [hasStarted, setHasStarted] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const obs = new IntersectionObserver(
            ([entry]) => { if (entry.isIntersecting && !hasStarted) setHasStarted(true); },
            { threshold: 0.3 }
        );
        obs.observe(el);
        return () => obs.disconnect();
    }, [hasStarted]);

    useEffect(() => {
        if (!hasStarted) return;
        const start = performance.now();
        const animate = (now: number) => {
            const elapsed = now - start;
            const progress = Math.min(elapsed / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            setCount(Math.round(eased * target));
            if (progress < 1) requestAnimationFrame(animate);
        };
        requestAnimationFrame(animate);
    }, [hasStarted, target, duration]);

    return <div ref={ref} className="stat-number">{count.toLocaleString()}{suffix}</div>;
}


// ─── Feature Showcase Data ────────────────────────────────────
const SHOWCASE_FEATURES = [
    {
        badge: 'Dashboard',
        badgeIcon: <LayoutDashboard size={14} />,
        title: 'Dashboard Overview',
        description: <>Upcoming matches, recent results, league standings, roster stats — everything at a glance on one page.</>,
        highlights: ['League Standings', 'Recent Results', 'Team Roster Stats', 'Upcoming Matches'],
        highlightIcon: <CheckCircle2 size={14} />,
        image: '/Showcase/Dashboard_demo.png',
    },
    {
        badge: 'Servers',
        badgeIcon: <Server size={14} />,
        title: 'Game Servers & Practice Modes',
        description: <>Spin up a CS2 server to train utility, run team drills, or host scrims. Demos are recorded automatically and pushed to your team space directly after the session.</>,
        highlights: ['Instant Boot', 'Automated Demos', 'Custom Plugins', 'Prefire Modes'],
        highlightIcon: <CheckCircle2 size={14} />,
        image: '/Showcase/GameServer_Demo.png',
    },
    {
        badge: 'Scouting',
        badgeIcon: <Target size={14} />,
        title: 'Opponent Scouting',
        description: <>Analyze upcoming opponent tendencies. By tracking patterns like AWP peaks and site setups across up to {PREMIUM_TEAM_SCOUT_MAX_DEMOS_PER_ANALYSIS} demos layered simultaneously, you can easily identify critical habits. Find vulnerabilities across multiple matches and instantly jump into the exact rounds to break down the play.</>,
        highlights: ['Threat Calculator', 'Map-by-Map Analysis', 'Counter-Strats'],
        highlightIcon: <CheckCircle2 size={14} />,
        image: '/Showcase/Analyze_demo2.png',
    },
    {
        badge: 'Analysis',
        badgeIcon: <Play size={14} />,
        title: 'Demo Viewer',
        description: <>Review matches in the Demo Viewer with a tactical 2D overview and 3D player perspectives. Follow all 10 players tick by tick, see engagements unfold, and analyze rotations — a powerful visual tool to understand exactly what happened.<br/><br/><span style={{ color: '#a855f7', fontWeight: 600 }}>Use Watch Together to synchronize playback with your teammates and review demos collaboratively.</span></>,
        highlights: ['Tick-by-Tick Playback', 'All 10 Players', 'Round Navigator', 'Auto-Sync from Faceit'],
        highlightIcon: <CheckCircle2 size={14} />,
        image: '/Showcase/Demoplayer_Demo.png',
    },
    {
        badge: 'Strategy',
        badgeIcon: <BookOpen size={14} />,
        title: 'Tactical Stratbook',
        description: <>Build your team's playbook directly on interactive 2D radar maps. Place player positions, draw movement paths, mark utility throws, and attach detailed notes — organized by map and side.<br/><br/><span style={{ color: '#a855f7', fontWeight: 600 }}>Use dry-run servers to import your dry-run strategies directly into the stratbook.</span></>,
        highlights: ['Interactive 2D Maps', 'Position Markers', 'Utility Throws', 'Per-Map Organization'],
        highlightIcon: <CheckCircle2 size={14} />,
        image: '/Showcase/Stratbook_Demo.png',
    },
    {
        badge: 'Leagues',
        badgeIcon: <Trophy size={14} />,
        title: 'Season Tracking & League Import',
        description: <>Import Faceit Championships and ESEA Leagues. Automatically track opponents, recent results, and live league standings to keep your entire competitive season intuitively organized from the first match to the grand final.</>,
        highlights: ['One-Click Import', 'Auto Demo Sync', 'Standings Tracker', 'ESEA Support'],
        highlightIcon: <CheckCircle2 size={14} />,
        image: '/Showcase/League_Demo.png',
    },
    {
        badge: 'Preparation',
        badgeIcon: <Crosshair size={14} />,
        title: 'Opponent Preparation',
        description: <>Generate comprehensive game plans against upcoming opponents. Note key weaknesses, map vetos, and match-specific strategies for your roster. Setup tailored, individual gameplans for specific enemies to prepare your team precisely.</>,
        highlights: ['Game Plans', 'Veto Simulator', 'Weakness Notes'],
        highlightIcon: <CheckCircle2 size={14} />,
        image: '/Showcase/Preperation_Demo.png',
    },
    {
        badge: 'Roadmap',
        badgeIcon: <Map size={14} />,
        title: 'Tactical Roadmap',
        description: <>Based on recent results, practice volume, and stratbook progress, your team's Confidence Score is calculated and matched against the opponent's threat level—an advanced algorithm will then automatically generate a prioritized map practice roadmap for your upcoming matches.</>,
        highlights: ['Season Roadmap', 'Confidence Levels', 'Risk Assessments'],
        highlightIcon: <CheckCircle2 size={14} />,
        image: '/Showcase/Roadmap_Demo.png',
    },
    {
        badge: 'Competitions',
        badgeIcon: <Swords size={14} />,
        title: 'Scrims & Opponent Search',
        description: <>Find practice opponents quickly and schedule scrims directly within the platform. Perfect for getting your team ready before the next league match.</>,
        highlights: ['Pracc Finder', 'Schedule Management', 'Direct Matchups', 'Opponent Search'],
        highlightIcon: <CheckCircle2 size={14} />,
        image: '/Showcase/Scrims_Demo.png',
    },
];

// ─── Bento Features Data ──────────────────────────────────────
const BENTO_FEATURES = [
    { icon: <Calendar size={24} />, title: 'Team Calendar', desc: 'Shared availability for your entire roster. Instantly spot overlaps and schedule practices or matches in seconds.' },
    { icon: <Database size={24} />, title: 'Demo Archive & Deep Analysis', desc: 'Every Faceit and practice demo stored permanently. Deep stats tracking: K/D, ADR, HLTV rating, utility damage across seasons.' },
    { icon: <Bomb size={24} />, title: 'Grenade Lineup Library', desc: 'Mark positions on the radar and attach videos for every smoke, flash, molotov, and HE your team uses.' },
    { icon: <Puzzle size={24} />, title: 'Browser Extension', desc: 'One-click demo downloads from Faceit match rooms. Transferred directly into your demo analysis tool.' },
    { icon: <Send size={24} />, title: 'Telegram Bot', desc: 'Automatic match reminders, availability updates, and team communication — without opening a browser.' },
    { icon: <UserCheck size={24} />, title: 'Player Statistics', desc: 'Individual heatmaps, aim analysis, and frame-by-frame demo breakdowns for every team member over time.' },
    { icon: <Search size={24} />, title: 'Member Search', desc: 'Find teammates or substitute players quickly within the platform network.' },
    { icon: <Zap size={24} />, title: 'Public Deathmatch Server', desc: 'Jump into our public deathmatch servers for a quick high-tickrate warm-up session before the real match starts.' },
    { icon: <Monitor size={24} />, title: 'Second Screen Mode', desc: 'Display your preparations and strategies efficiently on a second screen to gain a tactical advantage in-game.' },
    { icon: <Target size={24} />, title: 'Individual Enemy Preparation', desc: 'Setup individual gameplans and tailored strategy books for upcoming opponent teams.' },
    { icon: <PlusCircle size={24} />, title: 'And Much More...', desc: 'Constant platform updates, powerful new features, and a growing esports ecosystem built for your competitive success.' }
];

// ─── Testimonials Data ────────────────────────────────────────
const TESTIMONIALS = [
    {
        name: 'xKratos',
        elo: '2,847',
        level: 10,
        avatar: 'avatar-1',
        initials: 'xK',
        quote: 'StratSync completely changed how we prepare for league matches. The stratbook with the 2D maps is insane — we used to share strats in Discord messages. Now everything is organized and visual.',
        role: 'IGL — Team Captain',
    },
    {
        name: 'n1QUE',
        elo: '2,134',
        level: 8,
        avatar: 'avatar-2',
        initials: 'nQ',
        quote: 'The demo archive alone is worth it. Every Faceit match automatically stored and analyzed. I can review my positioning round by round and actually see where I need to improve.',
        role: 'AWPer — Entry Fragger',
    },
    {
        name: 'st4tic',
        elo: '1,856',
        level: 7,
        avatar: 'avatar-3',
        initials: 'sT',
        quote: 'We went from struggling to coordinate practice times to having everything in one calendar. The Telegram bot pings everyone automatically. No more "I didn\'t know we had practice" excuses.',
        role: 'Support — Team Manager',
    },
];

interface MaintenancePageProps {
    onAdminLogin: (token: string) => void;
}

export default function MaintenancePage({ onAdminLogin }: MaintenancePageProps) {
    const [showFeatures, setShowFeatures] = useState(false);
    const { t, i18n } = useTranslation();
    const contentRef = useRef<HTMLDivElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const { videoRef: heroVideoRef } = useHeroVideo();
    const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

    // Legal modal state
    const [legalModal, setLegalModal] = useState<'imprint' | 'privacy' | null>(null);

    // Admin login states
    const [showLogin, setShowLogin] = useState(false);
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    // Show features immediately and unlock scroll
    useEffect(() => {
        document.documentElement.style.overflow = 'auto';
        document.body.style.overflow = 'auto';
        setShowFeatures(true);
        return () => {
            document.documentElement.style.overflow = '';
            document.body.style.overflow = '';
        };
    }, []);

    // IntersectionObserver for scroll animations
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) entry.target.classList.add('visible');
                });
            },
            { threshold: 0.1, rootMargin: '0px 0px -80px 0px' }
        );
        const elements = document.querySelectorAll('.fade-in-section, .slide-in-left, .slide-in-right');
        elements.forEach(el => observer.observe(el));
        return () => observer.disconnect();
    }, [showFeatures]);

    // Mouse parallax tracking
    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            setMousePos({
                x: (e.clientX - window.innerWidth / 2) / 60,
                y: (e.clientY - window.innerHeight / 2) / 60,
            });
        };
        window.addEventListener('mousemove', handleMouseMove);
        return () => window.removeEventListener('mousemove', handleMouseMove);
    }, []);

    // Animated particles
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        let animationFrameId: number;
        const resize = () => { canvas.width = window.innerWidth; canvas.height = window.innerHeight; };
        window.addEventListener('resize', resize);
        resize();

        class Particle {
            x = 0; y = 0; vx = 0; vy = 0; size = 0; life = 0; decay = 0; color = '';
            constructor() { this.reset(); }
            reset() {
                this.x = Math.random() * canvas!.width;
                this.y = canvas!.height + Math.random() * 100;
                this.vx = (Math.random() - 0.5) * 0.8;
                this.vy = -Math.random() * 1.5 - 0.3;
                this.size = Math.random() * 2.5 + 0.5;
                this.life = 1;
                this.decay = Math.random() * 0.008 + 0.003;
                const r = Math.random();
                this.color = r > 0.7 ? '#7b8cff' : r > 0.4 ? '#8b5cf6' : '#ffffff';
            }
            update() { this.x += this.vx; this.y += this.vy; this.life -= this.decay; if (this.life <= 0) this.reset(); }
            draw() {
                if (!ctx) return;
                ctx.globalAlpha = this.life * 0.4;
                ctx.fillStyle = this.color;
                ctx.beginPath();
                ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
                ctx.fill();
            }
        }

        const particles: Particle[] = [];
        for (let i = 0; i < 50; i++) particles.push(new Particle());
        const animate = () => {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            particles.forEach(p => { p.update(); p.draw(); });
            animationFrameId = requestAnimationFrame(animate);
        };
        animate();
        return () => { cancelAnimationFrame(animationFrameId); window.removeEventListener('resize', resize); };
    }, []);

    const handleAdminLogin = async () => {
        setError('');
        setLoading(true);
        try {
            const { data } = await axios.post(`${API}/auth/login`, { username, password });
            const token = data.access_token;
            if (!token) {
                setError('Invalid credentials.');
                setLoading(false);
                return;
            }
            const userRes = await axios.get(`${API}/users/me`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!userRes.data?.isAdmin) {
                setError('Admin access required.');
                setLoading(false);
                return;
            }
            localStorage.setItem('token', token);
            axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
            onAdminLogin(token);
        } catch (err: any) {
            if (err.response?.status === 429) {
                setError('Too many login attempts. Please wait a minute and try again.');
            } else {
                setError('Invalid credentials.');
            }
        }
        setLoading(false);
    };

    const scrollToContent = () => { contentRef.current?.scrollIntoView({ behavior: 'smooth' }); };

    return (
        <div className="login-page-container">


            <div className="bg-grid"></div>
            <div className="bg-gradient" style={{
                transform: `translate(${mousePos.x * 0.25}px, ${mousePos.y * 0.25}px) rotate(0deg) scale(1.5)`,
                transition: 'transform 0.15s ease-out'
            }}></div>

            {/* Hero background — smooth ping-pong frame playback */}
            <video
                ref={heroVideoRef}
                muted
                playsInline
                className="fixed inset-0 pointer-events-none"
                style={{
                    width: '100vw', height: '100vh', objectFit: 'cover',
                    opacity: 0.15,
                    transform: `scale(1.12) translate(${mousePos.x * 0.4}px, ${mousePos.y * 0.4}px)`,
                    transition: 'transform 0.1s ease-out',
                    zIndex: 0,
                }}
            />

            {/* Animated particle canvas */}
            <canvas ref={canvasRef} className="fixed inset-0 pointer-events-none" style={{ zIndex: 2, background: 'transparent' }} />

            {/* HUD scanlines */}
            <div className="fixed inset-0 pointer-events-none" style={{ zIndex: 1, opacity: 0.08 }}>
                <div style={{
                    width: '100%', height: '100%',
                    backgroundImage: 'linear-gradient(rgba(18,16,16,0) 50%, rgba(0,0,0,0.25) 50%), linear-gradient(90deg, rgba(95,115,255,0.04), rgba(139,92,246,0.02), rgba(95,115,255,0.04))',
                    backgroundSize: '100% 2px, 3px 100%'
                }} />
            </div>

            {/* Gradient depth overlays */}
            <div className="fixed inset-0 pointer-events-none" style={{
                background: 'linear-gradient(to bottom, rgba(10,14,26,0.7) 0%, transparent 30%, transparent 70%, rgba(10,14,26,0.8) 100%)',
                zIndex: 2
            }} />
            <div className="fixed inset-0 pointer-events-none" style={{
                background: 'linear-gradient(to right, rgba(10,14,26,0.9) 0%, transparent 20%, transparent 80%, rgba(10,14,26,0.9) 100%)',
                zIndex: 2
            }} />

            {/* ══════════════════════════════════════════════════════
                HERO SECTION
               ══════════════════════════════════════════════════════ */}
            <section className="hero">
                <div className="landing-container">
                    <div className="hero-layout">
                        <div className="hero-content">
                            <div className="mb-8 reveal-stagger delay-1 relative inline-block">
                                <img 
                                    src="/images/Stratsync_Logo.webp" 
                                    alt="" 
                                    loading="eager"
                                    className="absolute pointer-events-none" 
                                    style={{
                                        width: '400px',
                                        height: 'auto',
                                        maxWidth: 'max-content',
                                        right: '-80px',
                                        top: '40%',
                                        transform: 'translateY(-50%)',
                                        opacity: 0.12,
                                        zIndex: 0,
                                        filter: 'blur(1px)'
                                    }} 
                                />
                                <div style={{ position: 'relative', zIndex: 1 }}>
                                    <h1 className="hero-title tracking-tighter italic">STRATSYNC&nbsp;</h1>
                                    <p className="text-[18px] text-[#7b8cff] font-bold tracking-[2px] uppercase">CLOSED BETA</p>
                                </div>
                            </div>
                            <h2 className="text-[clamp(48px,8vw,72px)] font-black leading-[1.2] mb-6 bg-gradient-to-br from-white to-[#a0b0ff] bg-clip-text text-transparent reveal-stagger delay-2">
                                Currently<br />
                                Under<br />
                                Development
                            </h2>
                            <p className="hero-description reveal-stagger delay-2">
                                We are actively working on building the ultimate tactical and management platform for competitive CS2 teams. 
                                Thank you for your patience as we prepare everything for launch.
                            </p>
                            <div className="cta-buttons reveal-stagger delay-3">
                                <button onClick={scrollToContent} className="btn-landing btn-primary-landing">
                                    Explore Features
                                    <ChevronRight size={18} />
                                </button>
                            </div>
                        </div>

                        {/* Development Notice Card */}
                        <div className="login-card-landing" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '50px 30px' }}>
                            <div style={{
                                width: 80, height: 80, borderRadius: 24,
                                background: 'rgba(123,140,255,0.1)', border: '1px solid rgba(123,140,255,0.2)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                margin: '0 auto 24px',
                            }}>
                                <Wrench size={36} color="#7b8cff" />
                            </div>
                            <h3 style={{ color: 'white', fontSize: '24px', fontWeight: 800, margin: '0 0 12px', letterSpacing: '-0.5px' }}>
                                Launching Soon
                            </h3>
                            <p style={{ color: '#94a3b8', fontSize: '14px', lineHeight: 1.6, marginBottom: '24px', textAlign: 'center' }}>
                                The platform is currently in closed development. Check back later or explore the features we are building below.
                            </p>
                            <div style={{
                                display: 'inline-flex', alignItems: 'center', gap: 8, padding: '8px 16px',
                                background: 'rgba(245, 158, 11, 0.1)', borderRadius: '100px',
                                border: '1px solid rgba(245, 158, 11, 0.2)'
                            }}>
                                <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#f59e0b', animation: 'pulse 2s infinite' }} />
                                <span style={{ fontSize: 13, color: '#f59e0b', fontWeight: 600 }}>Development in Progress</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Scroll indicator */}
                <div className="scroll-indicator" onClick={scrollToContent}>
                    <span>Discover More</span>
                    <ChevronDown size={20} />
                </div>
            </section>

            {/* ══════════════════════════════════════════════════════
                CONTENT BELOW HERO
               ══════════════════════════════════════════════════════ */}
            <div
                ref={contentRef}
                style={{
                    opacity: showFeatures ? 1 : 0,
                    transform: showFeatures ? 'translateY(0)' : 'translateY(30px)',
                    transition: 'opacity 0.8s ease-out, transform 0.8s ease-out',
                    pointerEvents: showFeatures ? 'auto' : 'none',
                }}
            >


                {/* ── Feature Showcase — Alternating Left/Right ── */}
                <section className="showcase-section">
                    <div className="landing-container">
                        <div className="section-header-landing fade-in-section">
                            <div className="section-badge-landing">Platform</div>
                            <h2 className="section-title-landing">Built for Competitive Teams</h2>
                            <p className="section-description-landing">
                                Every tool designed around how serious CS2 teams actually prepare, practice, and compete.
                            </p>
                        </div>

                        {SHOWCASE_FEATURES.map((feature, i) => (
                            <div key={i}>
                                <div className={`showcase-row ${i % 2 !== 0 ? 'reverse' : ''}`}>
                                    {/* Image */}
                                    <div className={`showcase-image ${i % 2 === 0 ? 'slide-in-left' : 'slide-in-right'}`}>
                                        {feature.image ? (
                                            <img src={feature.image} alt={feature.title} className="showcase-screenshot" />
                                        ) : (
                                            <div className="showcase-image-placeholder">
                                                <ImageIcon size={40} />
                                                <span>Screenshot Coming Soon</span>
                                            </div>
                                        )}
                                    </div>

                                    {/* Text */}
                                    <div className={`showcase-text ${i % 2 === 0 ? 'slide-in-right' : 'slide-in-left'}`}>
                                        <div className="showcase-badge">
                                            {feature.badgeIcon}
                                            {feature.badge}
                                        </div>
                                        <h3>{feature.title}</h3>
                                        <p>{feature.description}</p>
                                        <div className="showcase-highlights">
                                            {feature.highlights.map((hl, j) => (
                                                <div key={j} className="showcase-highlight-tag">
                                                    {feature.highlightIcon}
                                                    {hl}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                                {i < SHOWCASE_FEATURES.length - 1 && <div className="showcase-divider" />}
                            </div>
                        ))}
                    </div>
                </section>

                {/* ── Bento Grid — More Features ── */}
                <section className="bento-section">
                    <div className="landing-container">
                        <div className="section-header-landing fade-in-section">
                            <div className="section-badge-landing">And More</div>
                            <h2 className="section-title-landing">Everything Else You Need</h2>
                            <p className="section-description-landing">
                                From scheduling to communication — the tools that keep your team running smoothly every day.
                            </p>
                        </div>

                        <div className="bento-grid">
                            {BENTO_FEATURES.map((feature, i) => (
                                <div key={i} className="bento-card fade-in-section">
                                    <div className="bento-icon">{feature.icon}</div>
                                    <h4>{feature.title}</h4>
                                    <p>{feature.desc}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* ── Testimonials ── */}
                <section className="testimonials-section">
                    <div className="landing-container">
                        <div className="section-header-landing fade-in-section">
                            <div className="section-badge-landing">Trusted by Players</div>
                            <h2 className="section-title-landing">What Players Say</h2>
                            <p className="section-description-landing">
                                Teams across all skill levels use StratSync to organize, prepare, and dominate their leagues.
                            </p>
                        </div>

                        <div className="testimonials-grid">
                            {TESTIMONIALS.map((t, i) => (
                                <div key={i} className="testimonial-card fade-in-section">
                                    <div className="testimonial-header">
                                        <div className={`testimonial-avatar ${t.avatar}`}>
                                            {t.initials}
                                        </div>
                                        <div className="testimonial-info">
                                            <div className="testimonial-name">{t.name}</div>
                                            <div className="testimonial-elo">
                                                <img src="/icons/socials/Icon_Faceit.png" alt="" style={{ width: '12px', height: '12px' }} />
                                                {t.elo} ELO — Level {t.level}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="testimonial-quote">"{t.quote}"</div>
                                    <div className="testimonial-role">{t.role}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* ── CTA Section ── */}
                <section className="cta-section">
                    <div className="landing-container">
                        <div className="cta-wrapper fade-in-section">
                            <h2 className="cta-title">Prepare for Launch</h2>
                            <p className="cta-subtitle">
                                The platform is currently being rigorously tested by our closed-beta teams. We will be opening up to the public soon.
                            </p>
                            <div className="cta-buttons">
                                <button className="cta-btn" style={{ background: 'rgba(255,255,255,0.1)', cursor: 'default' }}>
                                    Launch Timing TBA
                                </button>
                            </div>
                        </div>
                    </div>
                </section>

                {/* ── Footer ── */}
                <footer className="footer-landing">
                    <div className="landing-container">
                        <div className="footer-content-landing">
                            <div className="footer-logo-landing italic">STRATSYNC</div>
                            <div className="footer-links-landing" style={{ position: 'relative' }}>
                                <button onClick={() => setLegalModal('imprint')} style={{ background: 'none', border: 'none', color: '#94a3b8', fontSize: 13, cursor: 'pointer', padding: 0 }}>Imprint</button>
                                <button onClick={() => setLegalModal('privacy')} style={{ background: 'none', border: 'none', color: '#94a3b8', fontSize: 13, cursor: 'pointer', padding: 0 }}>Privacy Policy</button>
                                <button
                                    onClick={() => setShowLogin(!showLogin)}
                                    style={{
                                        display: 'flex', alignItems: 'center', gap: 6,
                                        padding: '8px 16px', borderRadius: 6,
                                        border: '1px solid rgba(255,255,255,0.08)',
                                        background: 'rgba(15,21,35,0.8)', backdropFilter: 'blur(10px)',
                                        color: '#94a3b8', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                                        transition: 'all 0.2s',
                                    }}
                                    onMouseEnter={e => (e.currentTarget.style.color = '#fff')}
                                    onMouseLeave={e => (e.currentTarget.style.color = '#94a3b8')}
                                >
                                    <Shield size={14} /> Admin
                                </button>
                                {showLogin && (
                                    <div style={{
                                        position: 'absolute', bottom: '100%', right: 0, marginBottom: 8,
                                        width: 320, padding: 24, borderRadius: 12,
                                        background: 'rgba(15,21,35,0.98)', border: '1px solid rgba(255,255,255,0.08)',
                                        boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
                                    }}>
                                        <h3 style={{ fontSize: 14, fontWeight: 700, color: '#fff', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                                            <LogIn size={16} /> Admin Access
                                        </h3>
                                        <input
                                            placeholder="Admin username" value={username} onChange={e => setUsername(e.target.value)}
                                            autoComplete="username"
                                            style={{
                                                width: '100%', padding: '10px 12px', borderRadius: 6,
                                                border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(0,0,0,0.3)',
                                                color: '#fff', fontSize: 13, marginBottom: 8, boxSizing: 'border-box',
                                            }}
                                        />
                                        <input
                                            type="password" placeholder="Password" value={password} onChange={e => setPassword(e.target.value)}
                                            onKeyDown={e => { if (e.key === 'Enter') handleAdminLogin(); }}
                                            style={{
                                                width: '100%', padding: '10px 12px', borderRadius: 6,
                                                border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(0,0,0,0.3)',
                                                color: '#fff', fontSize: 13, marginBottom: 12, boxSizing: 'border-box',
                                            }}
                                        />
                                        {error && <div style={{ color: '#ef4444', fontSize: 11, marginBottom: 8 }}>{error}</div>}
                                        <button onClick={handleAdminLogin} disabled={loading} style={{
                                            width: '100%', padding: '10px 20px', borderRadius: 6, border: 'none', background: '#7b8cff', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer', opacity: loading ? 0.6 : 1,
                                        }}>
                                            {loading ? 'Authenticating...' : 'Login'}
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="footer-copyright">
                            {new Date().getFullYear()} StratSync.
                        </div>
                    </div>
                </footer>

                {legalModal && createPortal(
                    <div style={{
                        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                        backgroundColor: 'rgba(10,14,26,0.8)', backdropFilter: 'blur(10px)',
                        zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        padding: 24
                    }}>
                        <div style={{
                            background: '#0f1523', border: '1px solid rgba(255,255,255,0.1)',
                            borderRadius: 16, width: '100%', maxWidth: 800, maxHeight: '90vh',
                            display: 'flex', flexDirection: 'column', overflow: 'hidden',
                            boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)'
                        }}>
                            <div style={{ padding: '20px 24px', borderBottom: '1px solid rgba(255,255,255,0.05)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                                    <h2 style={{ fontSize: 20, fontWeight: 700, color: '#fff', margin: 0 }}>
                                        {LEGAL_CONTENT[legalModal][i18n.language?.startsWith('de') ? 'de' : 'en'].title}
                                    </h2>
                                    <button 
                                        onClick={() => i18n.changeLanguage(i18n.language?.startsWith('de') ? 'en' : 'de')}
                                        style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: '#cbd5e1', cursor: 'pointer', fontSize: 13, padding: '4px 10px', borderRadius: 6, display: 'flex', alignItems: 'center', gap: 6, transition: 'all 0.2s ease' }}
                                        onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
                                        onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
                                    >
                                        <Globe size={14} />
                                        {i18n.language?.startsWith('de') ? 'Read in English' : 'Auf Deutsch lesen'}
                                    </button>
                                </div>
                                <button onClick={() => setLegalModal(null)} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: 28, lineHeight: 1, padding: 0 }}>&times;</button>
                            </div>
                            <div style={{ padding: 32, overflowY: 'auto', color: '#cbd5e1', fontSize: 15, lineHeight: 1.7 }} className="markdown-body" dangerouslySetInnerHTML={{ __html: markdownToHtml(LEGAL_CONTENT[legalModal][i18n.language?.startsWith('de') ? 'de' : 'en'].body) }} />
                        </div>
                    </div>,
                    document.body
                )}
            </div>
        </div>
    );
}
